import { ListingItem } from '@/components/property-details-page/MarketSituation/MarketSituation.vue';
import { getAcrHeaders, shouldInjectAcrHeaders } from '@/helpers/Api/anti-crawling/getAcrHeaders';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { GeoCoordinates } from '@smg-real-estate/hg-rets-schema';
import axios from 'axios';

export const YEAR_BUILT_RANGE_DELTA = 10;
export const YEAR_BUILT_RANGE_MIN = 1900;
export const YEAR_BUILT_RANGE_MAX = new Date().getFullYear();

export const LIVING_SPACE_PERCENTAGE_RANGE_DELTA = 0.2;
export const ROOMS_RANGE_DELTA = 1;

export const SEARCH_API_REQUEST_PAGE_SIZE = 100;

export interface ListingOfferings {
  buy: ListingItem[];
  rent: ListingItem[];
}

export const retrieveAllListingsFor = async (searchPayload: any): Promise<ListingItem[]> => {
  const listingsApiUrl = import.meta.env.VUE_APP_LISTINGS_SEARCH_API;

  const listings: ListingItem[] = [];
  let page = 0;

  const acrHeaders = shouldInjectAcrHeaders() ? await getAcrHeaders() : {};

  do {
    listings.push(
      ...((
        await axios.post(
          listingsApiUrl,
          { ...searchPayload, from: page * SEARCH_API_REQUEST_PAGE_SIZE },
          {
            headers: {
              'Content-Type': 'application/json',
              ...acrHeaders,
            },
          },
        )
      )?.data?.results || []),
    );
    page++;
  } while (listings.length === SEARCH_API_REQUEST_PAGE_SIZE * page);

  return listings || [];
};

export const retrieveSimilarListingsFor = async (
  property: PropertyValuationResult,
  listingType: 'RENT' | 'BUY',
): Promise<ListingItem[]> => {
  try {
    let listings = await retrieveAllListingsFor(createSearchPayload(property, listingType));

    if (!listings?.length) {
      listings = await retrieveAllListingsFor(createSearchPayload(property, listingType, true));
    }

    return listings;
  } catch (error) {
    console.error(`Failed to fetch similar ${listingType} listings:`, error);
    return [];
  }
};

export const acceptableListing = (
  listing: ListingItem,
  property: PropertyValuationResult,
): boolean => {
  const livingSpace = listing.listing.characteristics?.livingSpace;
  const numberOfRooms = listing.listing.characteristics?.numberOfRooms;

  const hasLivingSpaceAndRooms = numberOfRooms !== undefined || livingSpace !== undefined;

  const yearBuilt = listing.listing.characteristics?.yearBuilt;
  const validYear =
    yearBuilt === undefined ||
    (yearBuilt >= Math.max(property.yearBuild - YEAR_BUILT_RANGE_DELTA, YEAR_BUILT_RANGE_MIN) &&
      yearBuilt <= Math.min(property.yearBuild + YEAR_BUILT_RANGE_DELTA, YEAR_BUILT_RANGE_MAX));

  const validLivingSpace =
    livingSpace === undefined ||
    (livingSpace >= Math.max(0, property.livingSpace * (1 - LIVING_SPACE_PERCENTAGE_RANGE_DELTA)) &&
      livingSpace <= property.livingSpace * (1 + LIVING_SPACE_PERCENTAGE_RANGE_DELTA));

  const validRooms =
    numberOfRooms === undefined ||
    (numberOfRooms >= Math.max(0, property.room - ROOMS_RANGE_DELTA) &&
      numberOfRooms <= property.room + ROOMS_RANGE_DELTA);

  return hasLivingSpaceAndRooms && validYear && validLivingSpace && validRooms;
};

export const filterResultsOut = (
  listings: ListingItem[],
  property: PropertyValuationResult,
): ListingItem[] => listings.filter((listing) => acceptableListing(listing, property));

export const retrieveSimilarListings = async (
  property: PropertyValuationResult,
): Promise<ListingOfferings> => {
  if (!property.room && !property.livingSpace) {
    return { buy: [], rent: [] };
  }

  try {
    const [rentResponse, buyResponse] = await Promise.all([
      retrieveSimilarListingsFor(property, 'RENT'),
      retrieveSimilarListingsFor(property, 'BUY'),
    ]);

    return {
      rent: rentResponse,
      buy: buyResponse,
    };
  } catch (error) {
    console.error('Failed to fetch similar listings:', error);
    return { buy: [], rent: [] };
  }
};

export const retrieveFilteredListings = async (
  property: PropertyValuationResult,
): Promise<ListingOfferings> => {
  const similarListings = await retrieveSimilarListings(property);

  return {
    rent: filterResultsOut(similarListings.rent, property),
    buy: filterResultsOut(similarListings.buy, property),
  };
};

export const createSearchPayload = (
  property: PropertyValuationResult,
  offerType: 'RENT' | 'BUY',
  extendedArea = false,
) => {
  const { zipCode } = property;

  const location = {
    geoTags: [`geo-zipcode-${zipCode}`],
    ...(extendedArea ? { radius: 10 * 1000 } : {}),
  };

  return {
    query: {
      location,
      offerType,
      categories: [
        'APARTMENT',
        'FLAT',
        'ATTIC_FLAT',
        'BACHELOR_FLAT',
        'DUPLEX',
        'FURNISHED_FLAT',
        'LOFT',
        'MAISONETTE',
        'ROOF_FLAT',
        'SINGLE_ROOM',
        'STUDIO',
        'TERRACE_FLAT',
        'HOUSE',
        'SINGLE_HOUSE',
        'BIFAMILIAR_HOUSE',
        'BUNGALOW',
        'CASTLE',
        'CAVE_HOUSE',
        'CHALET',
        'ENGADINE_HOUSE',
        'FARM_HOUSE',
        'GRANNY_FLAT',
        'MULTIPLE_DWELLING',
        'RESIDENTIAL_COMMERCIAL_BUILDING',
        'ROW_HOUSE',
        'RUSTICO',
        'RUSTIC_HOUSE',
        'TERRACE_HOUSE',
        'VILLA',
      ],
    },
    from: 0,
    size: SEARCH_API_REQUEST_PAGE_SIZE,
    sortBy: 'dateCreated',
    sortDirection: 'desc',
    trackTotalHits: 1000,
  };
};

/**
 * Based on: https://henry-rossiter.medium.com/calculating-distance-between-geographic-coordinates-with-javascript-5f3097b61898
 */
export const haversineDistanceBetweenPoints = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371e3;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const deltaLon = lon2 - lon1;
  const deltaLambda = (deltaLon * Math.PI) / 180;
  return (
    Math.acos(Math.sin(p1) * Math.sin(p2) + Math.cos(p1) * Math.cos(p2) * Math.cos(deltaLambda)) * R
  );
};

export const areCoordinatesPresent = (loc?: GeoCoordinates): boolean =>
  loc?.latitude !== undefined && loc?.longitude !== undefined;

export const calculateDistance = (loc: GeoCoordinates, liCoordinates?: GeoCoordinates): number =>
  areCoordinatesPresent(liCoordinates)
    ? haversineDistanceBetweenPoints(
        loc.latitude!,
        loc.longitude!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        liCoordinates?.latitude!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        liCoordinates?.longitude!,
      )
    : Number.MAX_SAFE_INTEGER;

export const sortListingsByDistance = (
  loc: GeoCoordinates,
  properties: ListingItem[],
): ListingItem[] =>
  properties
    .map((li) => ({
      distance: calculateDistance(loc, li.listing.address?.geoCoordinates),
      listingItem: li,
    }))
    .sort(({ distance: distance1 }: any, { distance: distance2 }: any) => distance1 - distance2)
    .map(({ listingItem }: any) => listingItem);
